import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { checkUserRole } from '@/utils/authHelpers';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * Component that redirects users based on their role:
 * - Admin users go to /admin
 * - Entrepreneurs go to /entrepreneur or /onboarding
 * - Investors go to /investor or /onboarding
 * - Others go to homepage
 */
export default function RoleRedirect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkAndRedirect() {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        console.log('Checking role for redirect:', user.id, user.email);
        const role = await checkUserRole(user.id, user.email || '');
        console.log('Role determined:', role);

        // Admin users bypass onboarding completely
        if (role === 'admin') {
          navigate('/admin');
          return;
        }

        // For entrepreneurs and investors, check onboarding status
        if (role === 'entrepreneur' || role === 'investor') {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('user_id', user.id)
            .single();

          if (profileError) {
            console.error('Error checking onboarding status:', profileError);
            toast.error('Error checking user profile');
            navigate('/');
            return;
          }

          // Redirect to onboarding if not completed
          if (!profile.onboarding_completed) {
            navigate('/onboarding');
            return;
          }

          // If onboarding is completed, redirect to role-specific dashboard
          if (role === 'entrepreneur') {
            navigate('/entrepreneur');
          } else {
            navigate('/investor');
          }
          return;
        }

        // Default redirect to home for unknown roles
        navigate('/');
      } catch (error) {
        console.error('Error during role redirect:', error);
        toast.error('Error determining user access');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    }

    checkAndRedirect();
  }, [user, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Redirecting...</h2>
          <p className="text-gray-500">Please wait while we set up your dashboard</p>
        </div>
      </div>
    );
  }

  return null;
}
